import React, { Dispatch, SetStateAction } from 'react';
import PropTypes from 'prop-types';
import { noop } from 'lodash';

import { OrderInterface, TicketInterface } from '../types/app';

type TicketDistroStateHelp = {
  isHelpVisible: boolean;
  helpIsSuccess: boolean;
  helpIsError: boolean;
  helpTicketId: number;
  helpOrder: number;
};

type TicketDistroStateTickets = {
  dataLoaded: boolean;
  dataLoading: boolean;
  walkUpMode: boolean;
  orders: { items: OrderInterface[] } | null;
  assignedTickets?: { items: TicketInterface[] } | null;
  ticketDialogVisible: boolean;
  selectedTicket: TicketInterface | null;
};

export type TicketDistroState = {
  isLoggedIn: boolean;
  isLoggingIn: boolean;
  ticketNoLongerAvailable?: boolean;
} & TicketDistroStateTickets &
  TicketDistroStateHelp;

export type SetTicketDistroState = Dispatch<SetStateAction<Partial<TicketDistroState> | undefined>>;

export type TicketDistroContextInfo = {
  state: Partial<TicketDistroState>;
  actions: {
    setState: SetTicketDistroState;
    clearState: Function;
  };
};

interface ContextProviderProps {
  children: JSX.Element[] | JSX.Element;
  contextName: string;
  stateContext: React.Context<{
    ticketDistroContext: TicketDistroContextInfo;
  }>;
  initialState: TicketDistroContextInfo['state'];
}

export const TicketDistroContext = React.createContext<{ ticketDistroContext: TicketDistroContextInfo }>({
  ticketDistroContext: {
    state: {},
    actions: {
      setState: noop as SetTicketDistroState,
      clearState: noop,
    },
  },
});

export const INITIAL_DATA: TicketDistroContextInfo['state'] = {
  orders: null,
  assignedTickets: null,
  dataLoaded: false,
  dataLoading: false,
  isLoggedIn: false,
  isLoggingIn: false,
  ticketNoLongerAvailable: false,
  helpIsError: false,
  ticketDialogVisible: false,
  helpIsSuccess: false,
  helpOrder: undefined,
  helpTicketId: undefined,
  selectedTicket: undefined,
  walkUpMode: false,
  isHelpVisible: false,
};

export class ContextProvider extends React.Component<ContextProviderProps, object> {
  static propTypes = {
    children: PropTypes.node.isRequired,
    contextName: PropTypes.string.isRequired,
    stateContext: PropTypes.shape({}).isRequired,
    initialState: PropTypes.shape({}).isRequired,
  };

  state = { ...this.props.initialState };

  setStatePassThrough = (param: Partial<TicketDistroState>, callback?: () => void): void => {
    this.setState(param, callback);
  };

  clearStatePassThrough = (): void => {
    this.setState({
      ...this.props.initialState,
    });
  };

  actions = {
    setState: this.setStatePassThrough,
    clearState: this.clearStatePassThrough,
  };

  render(): JSX.Element {
    const { stateContext, contextName, children } = this.props;
    const StateContext = stateContext;
    const value = {
      [contextName]: {
        state: this.state,
        actions: this.actions,
      },
    };

    return <StateContext.Provider value={value}>{children}</StateContext.Provider>;
  }
}
